import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import Process from '@/pages/home/components/Process';
import OnboardingExperience from '@/pages/home/components/OnboardingExperience';
import ProjectTimeline from '@/pages/home/components/ProjectTimeline';
import ClientSuccessScorecard from '@/pages/home/components/ClientSuccessScorecard';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

/* ── Page Hero ── */
function PageHero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-28 pb-16 md:pt-36 md:pb-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20corporate%20process%20background%20with%20flowing%20connected%20pathways%20in%20dual%20tones%2C%20warm%20amber%20and%20cool%20teal%20lines%20converging%2C%20deep%20navy%20base%2C%20minimalist%20professional%20composition%20suggesting%20two%20parallel%20tracks%20merging%20into%20one%20goal%2C%20suitable%20for%20website%20hero%20banner%20with%20white%20text%20overlay%2C%20elegant%20and%20modern%20feel&width=1600&height=900&seq=howitworks-hero-2&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/60 via-navy-900/70 to-navy-900/90"></div>
      </div>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-route-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Our Process</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Two Tracks,
            <br />
            <span className="text-primary-400">One Goal: Results</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-3xl mx-auto"
          >
            Whether you need a product built from scratch or a vetted engineer on your team next week — our processes are transparent, battle-tested, and built for speed.
          </motion.p>

          {/* Quick Jump Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8 md:mt-10"
          >
            <a
              href="#software-process"
              className="px-6 py-3 bg-white/10 border border-white/20 text-white text-sm font-semibold rounded-xl hover:bg-white/15 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-code-box-line"></i>
              Software Development Process
            </a>
            <a
              href="#talent-process"
              className="px-6 py-3 bg-white/10 border border-white/20 text-white text-sm font-semibold rounded-xl hover:bg-white/15 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-user-star-line"></i>
              Talent Placement Process
            </a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

/* ── Why Our Process ── */
function WhyOurProcess() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const values = [
    {
      icon: 'ri-flashlight-line',
      title: 'Speed Without Compromise',
      desc: '48-hour talent matching and 2-week agile sprints — the fastest delivery in the industry without cutting corners.',
      stat: '48h',
      statLabel: 'first matches or sprint plans',
    },
    {
      icon: 'ri-user-search-line',
      title: 'Human + AI Matching',
      desc: 'AI surfaces candidates and project scope. Senior recruiters and technical leads validate fit, architecture, and domain expertise.',
      stat: '94%',
      statLabel: 'first-match success rate',
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Structured Delivery',
      desc: 'Every engagement follows a defined playbook — from discovery and onboarding to sprint reviews and 14-day performance windows.',
      stat: '6',
      statLabel: 'standardized process phases',
    },
    {
      icon: 'ri-message-3-line',
      title: 'Always in the Loop',
      desc: 'Weekly updates, dedicated account managers, and direct Slack channels keep teams aligned on both tracks.',
      stat: '24/7',
      statLabel: 'account support',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-award-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Why Our Process Works</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Built for Clarity
            <span className="text-primary-600"> and Speed</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            We have refined our processes through hundreds of engagements. Every step is designed to reduce risk, increase transparency, and deliver outcomes faster.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {values.map((v, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${v.icon} text-xl text-white`}></i>
              </div>
              <div className="mb-3">
                <span className="text-2xl md:text-3xl font-bold text-primary-600">{v.stat}</span>
                <p className="text-xs text-gray-500 mt-0.5">{v.statLabel}</p>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{v.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Software Development Process ── */
function SoftwareDevProcess() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const steps = [
    {
      num: '01',
      title: 'Discovery & Strategy',
      desc: 'We validate your idea, research the market, define the MVP scope, and create a technical architecture plan with stack recommendations.',
      icon: 'ri-compass-discover-line',
      timeline: 'Week 1–2',
    },
    {
      num: '02',
      title: 'Design & Prototyping',
      desc: 'User research, wireframes, high-fidelity prototypes, and a scalable design system — all built for usability and brand consistency.',
      icon: 'ri-palette-line',
      timeline: 'Week 2–4',
    },
    {
      num: '03',
      title: 'Engineering Sprints',
      desc: 'Agile two-week sprints with CI/CD, daily standups, code reviews, and transparent progress dashboards you can track live.',
      icon: 'ri-code-s-slash-line',
      timeline: 'Week 4–12',
    },
    {
      num: '04',
      title: 'QA & Testing',
      desc: 'Automated and manual testing at every layer — unit, integration, performance, and security — before any release reaches production.',
      icon: 'ri-bug-line',
      timeline: 'Continuous',
    },
    {
      num: '05',
      title: 'Deployment & DevOps',
      desc: 'Cloud-native deployment on AWS, Azure, or GCP with auto-scaling, monitoring, and zero-downtime CI/CD releases.',
      icon: 'ri-rocket-line',
      timeline: 'Week 8–14',
    },
    {
      num: '06',
      title: 'Support & Growth',
      desc: 'Ongoing maintenance, feature enhancements, performance tuning, and 24/7 monitoring to keep your product competitive long-term.',
      icon: 'ri-tools-line',
      timeline: 'Ongoing',
    },
  ];

  return (
    <section id="software-process" className="py-16 md:py-24 bg-gray-50/50 relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-code-box-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Track 1</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Software Development
            <span className="text-primary-600"> Lifecycle</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            From idea to deployed product — a battle-tested development process designed to reduce risk, maintain quality, and ship on time.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.1, ...springTransition }}
              className="group bg-white rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="text-3xl font-black text-primary-200">{step.num}</div>
                <span className="text-xs font-semibold text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full border border-primary-100">
                  {step.timeline}
                </span>
              </div>
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${step.icon} text-xl text-white`}></i>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{step.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Talent Placement Section Header ── */
function TalentSectionHeader() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <section id="talent-process" className="pt-16 md:pt-24 pb-4 md:pb-6 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-user-star-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Track 2</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Talent Placement
            <span className="text-primary-600"> Process</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            A transparent, battle-tested process designed to get the right engineers working on your product in days — not months.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

/* ── How It Works CTA ── */
function HowItWorksCTA() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <section className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-6">
            <i className="ri-briefcase-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Ready to Start?</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4 md:mb-6">
            Build Software <span className="text-primary-400">or</span> Hire Talent
          </h2>
          <p className="text-base md:text-xl text-white/70 max-w-2xl mx-auto mb-8 md:mb-10">
            Whether you need a complete product built or a single engineer to join your team — we will recommend the right approach and get you started fast.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/software-development"
              className="px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Start a Project
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/talent-solutions"
              className="px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Browse Talent Pool
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function HowItWorksPage() {
  return (
    <PageLayout title="How It Works — Our Process" description="Discover our dual-track process: software development lifecycle from discovery to deployment, and our 48-hour talent matching process. Transparent, battle-tested, and built for speed." keywords="how software development works, engineer hiring process, 48 hour talent matching, agile software delivery, engineer vetting process, staff augmentation process, remote developer onboarding">
      <PageHero />
      <WhyOurProcess />
      <SoftwareDevProcess />
      <TalentSectionHeader />
      <Process />
      <OnboardingExperience />
      <ProjectTimeline />
      <ClientSuccessScorecard />
      <HowItWorksCTA />
    </PageLayout>
  );
}